/* Flooring Surgeons – "The Cheapest" / "The Most Expensive" by price per m² (owner, 4 Oct 2026).
   Shopify's price sorts use the pack price, so the theme orders by custom.price_m2 instead (key: snippets/fs-m2-key).
   - Page 1 with up to 250 results: the server already rendered the first cards in £/m² order and handed the rest of the
     order to "Show more" (fs-load-more, data-m2) as product URLs – this file fetches those cards.
   - More than 250 results, or a deep ?page=N link: the server renders Shopify's page hidden (#product-grid.fs-m2-pending);
     this file reads the full order from sections/fs-sort-index (same filters, all pages), then puts in the cards for
     page N and gives "Show more" the rest.
   Cards come from sections/fs-card-item (/products/<handle>?section_id=fs-card-item), fetched in parallel and cached. */
(() => {
  if (window.FSM2) return;
  const doc = document;
  const cache = new Map();

  const withSection = (url, id) => url + (url.includes('?') ? '&' : '?') + 'section_id=' + id;

  const card = (url) => {
    if (!cache.has(url)) {
      cache.set(url, fetch(withSection(url, 'fs-card-item'))
        .then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then((html) => new DOMParser().parseFromString(html, 'text/html').querySelector('li.grid__item'))
        .catch((e) => { cache.delete(url); throw e; }));
    }
    return cache.get(url).then((li) => li && doc.importNode(li, true));
  };
  const cards = (urls) => Promise.all(urls.map(card)).then((lis) => lis.filter(Boolean));

  // every result of the current collection / search (with its filters), sorted by the server-made keys
  const index = async () => {
    const params = new URLSearchParams(location.search);
    params.delete('page');
    params.delete('section_id');
    const page = (n) => {
      const p = new URLSearchParams(params);
      p.set('section_id', 'fs-sort-index');
      if (n > 1) p.set('page', n);
      return fetch(location.pathname + '?' + p.toString())
        .then((r) => r.text())
        .then((html) => JSON.parse(new DOMParser().parseFromString(html, 'text/html').querySelector('[data-fs-sort-index]').textContent));
    };
    const first = await page(1);
    const rest = await Promise.all(Array.from({ length: Math.max(0, first.pages - 1) }, (_, i) => page(i + 2)));
    const items = [first, ...rest].flatMap((d) => d.items);
    items.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
    return items.map((i) => i[1]);
  };

  const ready = (grid) => {
    grid.classList.remove('fs-m2-pending', 'loading');
    grid.closest('.collection')?.classList.remove('loading');
  };

  const run = async (grid) => {
    grid.dataset.fsM2Busy = '1';
    const page = parseInt(grid.dataset.fsM2Page, 10) || 1;
    const size = parseInt(grid.dataset.fsM2Size, 10) || 24;
    try {
      const order = await index();
      const lis = await cards(order.slice((page - 1) * size, page * size));
      if (!grid.isConnected) return; // a newer filter / sort answer replaced the grid meanwhile
      lis.forEach((li, i) => li.style.setProperty('--animation-order', i + 1));
      (grid.matches('ul') ? grid : grid.querySelector('ul.product-grid') || grid).replaceChildren(...lis);
      const more = grid.closest('#ProductGridContainer')?.querySelector('fs-load-more');
      if (more) more.setOrder ? more.setOrder(order.slice(page * size), page) : (more._m2 = [order.slice(page * size), page]);
    } catch (e) {
      console.warn('£/m² sort: showing Shopify order', e);
    } finally {
      if (grid.isConnected) ready(grid);
    }
  };

  const scan = () => doc.querySelectorAll('#product-grid.fs-m2-pending:not([data-fs-m2-busy])').forEach(run);
  let queued = false;
  const queue = () => { if (!queued) { queued = true; setTimeout(() => { queued = false; scan(); }); } }; // not rAF: also runs in a background tab
  const init = () => {
    scan();
    new MutationObserver(queue).observe(doc.body, { childList: true, subtree: true });
  };
  doc.readyState === 'loading' ? doc.addEventListener('DOMContentLoaded', init) : init();
  window.FSM2 = { cards, index };
})();
